import React, { useEffect, useState } from 'react'
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react-pro'
import LoadError from './LoadError'

// Read-only vote table in a modal. `load` returns a promise of the API response; it runs each
// time the modal opens with a new `load`, so the caller should memoise it per record.
const VoteDetailModal = ({ load, title, subtitle, columns, emptyLabel, onClose }) => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!load) return
    let current = true
    setRows([])
    setLoading(true)
    setError(false)
    load()
      .then((res) => {
        if (!current) return
        if (res.status === 200) setRows(res.data || [])
        else setError(true)
      })
      .catch(() => current && setError(true))
      .finally(() => current && setLoading(false))
    // A newer open (or a close) wins over a slow response for the previous record.
    return () => {
      current = false
    }
  }, [load, attempt])

  return (
    <CModal size="lg" visible={!!load} onClose={onClose}>
      <CModalHeader>
        <div>
          <CModalTitle>{title}</CModalTitle>
          {subtitle && <div className="vt-panel-note">{subtitle}</div>}
        </div>
      </CModalHeader>
      <CModalBody>
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading votes...</span>
          </div>
        ) : error ? (
          <LoadError
            message="We couldn't load these votes."
            onRetry={() => setAttempt((n) => n + 1)}
          />
        ) : rows.length === 0 ? (
          <div className="vt-empty">{emptyLabel}</div>
        ) : (
          <div className="table-responsive">
            <table className="vt-detail table align-middle mb-0">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className={col.numeric ? 'text-end' : undefined}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {columns.map((col, colIndex) => (
                      <td
                        key={col.key}
                        className={
                          col.numeric ? 'text-end' : colIndex === 0 ? 'vt-detail-name' : undefined
                        }
                      >
                        {row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Close
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default VoteDetailModal
